import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  refreshAccessToken,
  clearAuthData,
  isAccessTokenExpired,
} from "./auth";
import { AxiosRequestConfig } from "axios";
import { PaginationParams, PageResponse } from "../types";

// Define a custom interface to extend AxiosRequestConfig
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  skipAuthRedirect?: boolean;
}

// Determine the base URL based on the environment
const getBaseUrl = () => {
  if (process.env.NODE_ENV === "production") {
    return "https://api.parkwithparallel.com";
  }

  // For development, dynamically determine the backend URL
  const currentHost = window.location.hostname;
  const currentPort = window.location.port;

  // If accessing from localhost, use localhost for backend
  if (currentHost === "localhost" || currentHost === "127.0.0.1") {
    return "http://localhost:8085/ParkingWithParallel";
  }

  // If accessing from IP address (like from phone), use the same IP for backend
  return `http://${currentHost}:8085/ParkingWithParallel`;
};

// Create the main API instance
const api = axios.create({
  baseURL: getBaseUrl(),
  // LLM-backed endpoints (ai/send-message, agent actions) can legitimately take
  // 20–40s, so keep the timeout generous to avoid false "offline" states.
  timeout: 90000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{ resolve: Function; reject: Function }> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

// Request interceptor to add access token to all requests
api.interceptors.request.use(
  async (config: any) => {
    // Skip auth for certain endpoints
    const skipAuthEndpoints = [
      "/login",
      "/login/refresh",
      "/login/logout",
      "/password-reset",
    ];
    const isSkipAuthEndpoint = skipAuthEndpoints.some((endpoint) =>
      config.url?.includes(endpoint)
    );

    // Skip auth if explicitly requested
    if (config.skipAuthRedirect || isSkipAuthEndpoint) {
      return config;
    }

    // Check if access token is expired and refresh if needed
    if (isAccessTokenExpired()) {
      console.log("Access token expired, attempting refresh before request");

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          const refreshSuccess = await refreshAccessToken();
          if (!refreshSuccess) {
            throw new Error("Token refresh failed");
          }
          processQueue(null, getAccessToken());
        } catch (error) {
          processQueue(error, null);
          throw error;
        } finally {
          isRefreshing = false;
        }
      } else {
        // If already refreshing, wait for it to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            const token = getAccessToken();
            if (token) {
              config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
          })
          .catch((error) => {
            return Promise.reject(error);
          });
      }
    }

    // Add access token to request
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh on 401 errors
api.interceptors.response.use(
  (response) => {
    // If we get a successful response, clear any server offline state
    window.dispatchEvent(
      new CustomEvent("server-status", { detail: { status: "online" } })
    );
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Check if the error is due to network connectivity (server offline)
    if (!error.response) {
      if (error.code === "ECONNABORTED") {
        // Request timed out (likely long-running AI call). Do not toggle offline banner.
        console.warn("Request timed out before the server responded:", error);
        return Promise.reject(error);
      }
      // Network error, server is likely offline
      console.error("Network error, server may be offline:", error);
      window.dispatchEvent(
        new CustomEvent("server-status", { detail: { status: "offline" } })
      );
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response.status === 401 && !originalRequest._retry) {
      // Skip retry for auth endpoints to prevent infinite loops
      const skipRetryEndpoints = ["/login", "/login/refresh", "/login/logout"];
      const isSkipRetryEndpoint = skipRetryEndpoints.some((endpoint) =>
        originalRequest.url?.includes(endpoint)
      );

      if (isSkipRetryEndpoint) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          console.log("Received 401, attempting to refresh access token");
          const refreshSuccess = await refreshAccessToken();

          if (refreshSuccess) {
            const newToken = getAccessToken();
            if (newToken) {
              // Update the original request with new token
              originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
              processQueue(null, newToken);
              // Retry the original request
              return api(originalRequest);
            }
          }

          throw new Error("Token refresh failed");
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          processQueue(refreshError, null);

          // Clear auth data and redirect to login
          clearAuthData();

          // Only redirect if we're not already on the login page
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      } else {
        // If already refreshing, wait for it to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((refreshError) => {
            return Promise.reject(refreshError);
          });
      }
    }

    return Promise.reject(error);
  }
);

// User service functions
export const userService = {
  // Get current user details
  getCurrentUser: async () => {
    const response = await api.get("/users/current");
    return response.data;
  },

  // Get user by ID
  getUserById: async (userId: string) => {
    const response = await api.get(`/users/get-user-by-id/${userId}`);
    return response.data;
  },
};

// Lot service functions
export const lotService = {
  // Get all lots
  getAllLots: async () => {
    const response = await api.get("/parkinglots/get-all");
    return response.data;
  },

  // Get all lots with cursor-based pagination (superadmin usage)
  getAllLotsPaginated: async (
    cursor?: string | null,
    limit: number = 40,
    includeArchived?: boolean
  ) => {
    const params = new URLSearchParams();
    if (cursor) {
      params.append("cursor", cursor);
    }
    if (limit) {
      params.append("limit", String(limit));
    }
    if (includeArchived) {
      params.append("includeArchived", "true");
    }
    const query = params.toString();
    const url = query
      ? `/parkinglots/get-all-parking-lots-paginated?${query}`
      : "/parkinglots/get-all-parking-lots-paginated";
    const response = await api.get(url);
    return response.data;
  },

  // Get all lots with offset-based pagination (superadmin usage)
  getAllLotsOffsetPaginated: async (
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);
    if ((params as any).includeArchived) searchParams.append("includeArchived", "true");

    const response = await api.get(
      `/parkinglots/get-all-parking-lots-offset-paginated?${searchParams.toString()}`
    );
    return response.data.data;
  },

  // Get lots by user ID (lots where user is owner/operator/staff)
  getLotsByUserId: async (userId: string) => {
    try {
      const response = await api.get(`/parkinglots/get-lots-by-user/${userId}`);

      // Debug the response structure
      console.log("API Response:", response);
      console.log("Response data:", response.data);

      // Handle case where response.data might not be an array
      let lotsData = response.data;
      if (!Array.isArray(lotsData)) {
        // If data is not an array, try to extract the array from the response
        if (lotsData && typeof lotsData === "object") {
          // Try common response structures
          lotsData = lotsData.data || lotsData.lots || lotsData.items || [];
        } else {
          lotsData = [];
        }
      }

      // Transform the response data to match the expected Lot interface
      const lots = lotsData
        .filter((lot: any) => {
          const status = (lot?.lotStatus || lot?.accountStatus || "").toString().toLowerCase();
          return status !== "suspended" && status !== "archived";
        })
        .map((lot: any) => ({
          lotId: lot.lotId,
          displayLotId: lot.displayLotId,
          lotName: lot.lotName,
          address: lot.address,
          ownerCustomerId: lot.ownerCustomerId,
          geoLocation: lot.geoLocation,
          lotCapacity: lot.lotCapacity,
          listedDeviceIds: lot.listedDeviceIds,
          accountStatus: lot.accountStatus,
          accountCreated: lot.createdOn || lot.accountCreated || null,
          notificationRecipients: lot.notificationRecipients,
        }));

      return lots;
    } catch (error) {
      console.error("Error fetching lots by user ID:", error);
      throw error;
    }
  },

  // Get lot by ID
  getLotById: async (lotId: string) => {
    try {
      const response = await api.get(`/parkinglots/get-by-id/${lotId}`);
      return response.data.data; // Assuming the response structure has data.data
    } catch (error) {
      console.error("Error fetching lot data:", error);
      throw error;
    }
  },

  // Get current occupancy for a lot
  getCurrentOccupancy: async (lotId: string): Promise<number | null> => {
    try {
      const response = await api.get(
        `/parkinglots/get-current-occupancy/${lotId}`
      );
      if (
        response.data &&
        response.data.success &&
        typeof response.data.data === "number"
      ) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  },

  // Override current occupancy for a lot (superadmin only)
  overrideCurrentOccupancy: async (
    lotId: string,
    occupancy: number
  ): Promise<number> => {
    const response = await api.patch(`/parkinglots/override-current-occupancy`, {
      lotId,
      occupancy,
    });
    if (
      response.data &&
      response.data.success &&
      typeof response.data.data === "number"
    ) {
      return response.data.data;
    }
    return occupancy;
  },

  // Get operators for a lot
  getOperators: async (lotId: string) => {
    try {
      const response = await api.get(`/parkinglots/get-all-operators/${lotId}`);
      return response.data.data || response.data; // Handle different response structures
    } catch (error) {
      console.error("Error fetching operators:", error);
      throw error;
    }
  },

  // New method: Get all staff for a lot
  getAllStaff: async (lotId: string) => {
    try {
      const response = await api.get(`/parkinglots/get-all-staff/${lotId}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        return [];
      }
    } catch (error: any) {
      // Silently return empty array for any error
      return [];
    }
  },

  // Update lot
  updateLot: async (lotId: string, lotData: any) => {
    const response = await api.put(
      `/parkinglots/update-parking-lot/${lotId}`,
      lotData
    );
    return response.data;
  },

  // Soft-delete an archived lot
  deleteLot: async (lotId: string, deletedBy: string) => {
    const response = await api.delete(
      `/parkinglots/delete-parking-Lot/${lotId}/${encodeURIComponent(deletedBy)}`
    );
    return response.data;
  },

  // Enable registry for a lot
  enableRegistry: async (lotId: string, modifiedBy: string) => {
    const response = await api.post(
      `/parkinglots/enable-registry/${lotId}/${modifiedBy}`
    );
    return response.data;
  },

  // Disable registry for a lot
  disableRegistry: async (lotId: string, modifiedBy: string) => {
    const response = await api.post(
      `/parkinglots/disable-registry/${lotId}/${modifiedBy}`
    );
    return response.data;
  },

  // Get registry status for a lot
  getRegistryStatus: async (lotId: string): Promise<boolean> => {
    const response = await api.get(`/parkinglots/get-registry-status/${lotId}`);
    return response.data.data; // Return the boolean value from the data field
  },

  // Update auto force end time range for a lot
  updateAutoForceEndTimeRange: async (lotId: string, timeRange: number) => {
    const response = await api.patch(
      "/parkinglots/auto-force-end-time-range",
      null,
      {
        params: { lotId, timeRange },
      }
    );
    return response.data;
  },

  // Get notification recipients for a lot
  getNotificationRecipients: async (lotId: string) => {
    const response = await api.get(
      `/parkinglots/get-notification-recipients/${lotId}`
    );
    return response.data;
  },

  // Add notification recipient to a lot
  addNotificationRecipient: async (lotId: string, recipient: string) => {
    await api.post(
      `/parkinglots/add-notification-recipient/${lotId}`,
      recipient
    );
  },

  // Remove notification recipient from a lot
  removeNotificationRecipient: async (lotId: string, recipient: string) => {
    await api.delete(`/parkinglots/remove-notification-recipient/${lotId}`, {
      data: recipient,
    });
  },
};

// AI chat service helpers and types
const extractResponseData = <T>(response: any, fallbackMessage: string): T => {
  const payload = response?.data;
  if (payload?.success) {
    return payload.data as T;
  }
  throw new Error(payload?.message || fallbackMessage);
};

const buildAxiosError = (error: unknown, fallbackMessage: string): Error => {
  if (axios.isAxiosError(error)) {
    const backendMessage = error.response?.data?.message;
    if (backendMessage) {
      return new Error(backendMessage);
    }
  }
  if (error instanceof Error) {
    return error;
  }
  return new Error(fallbackMessage);
};

export interface AiRateInfo {
  day?: string;
  quota: number;
  used: number;
  resetAt?: string | null;
}

export interface AiConversationSummary {
  conversationId: string;
  lotId?: string;
  title?: string | null;
  status?: string | null;
  lastMessageAt?: string | null;
  createdOn?: string | null;
  modifiedOn?: string | null;
}

export interface AiMessageItem {
  messageId: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  contentJson?: any;
  createdOn?: string | null;
}

export interface AiMessagesPage {
  items: AiMessageItem[];
  nextAfter?: string | null;
  previousBefore?: string | null;
}

export interface AiAssistantMessage {
  messageId: string;
  conversationId: string;
  content: string;
  contentJson?: any;
  createdOn?: string | null;
}

export interface AiAgentActionConfirmResponse {
  taskId: string;
  status: string;
  assistantMessage?: AiAssistantMessage | null;
}

export interface AiSendMessageResponse {
  messageId: string;
  conversationId: string;
  assistantMessage: AiAssistantMessage | null;
  assistantMessages?: AiAssistantMessage[] | null;
  rate?: AiRateInfo | null;
}

export interface AiSendMessagePayload {
  conversationId: string;
  lotId?: string;
  content: string;
  mode: string;
  contentJson?: any;
  taggedAgentKeys?: string[];
  dataConsent?: boolean;
  images?: string[];
}

/**
 * Lightweight agent info for the chat agent picker.
 * Does not include sensitive fields like prompt, endpoints, etc.
 */
export interface AiAgentNameItem {
  agentKey: string;
  name: string;
  description?: string;
}

export interface AiMessagesRequestOptions {
  after?: string | null;
  before?: string | null;
  limit?: number;
}

export const aiChatService = {
  async getRateLimit(lotId: string): Promise<AiRateInfo | null> {
    if (!lotId) return null;
    try {
      const response = await api.get(`/ai/get-rate-limit/${lotId}`);
      return extractResponseData<AiRateInfo>(response, "Failed to load AI quota");
    } catch (error) {
      console.error("Error fetching AI rate limit", error);
      throw error;
    }
  },

  async getConversationsByLot(
    lotId: string,
    params?: { status?: string; limit?: number; page?: number },
  ): Promise<AiConversationSummary[]> {
    if (!lotId) return [];
    try {
      const response = await api.get(`/ai/get-conversations-by-lot/${lotId}`, {
        params,
      });
      return extractResponseData<AiConversationSummary[]>(response, "Failed to load AI conversations");
    } catch (error) {
      console.error("Error fetching AI conversations", error);
      throw error;
    }
  },

  async startConversation(lotId: string, title?: string): Promise<AiConversationSummary> {
    if (!lotId) {
      throw new Error("lotId is required to start a conversation");
    }
    try {
      const response = await api.post("/ai/start-conversation", {
        lotId,
        title,
      });
      return extractResponseData<AiConversationSummary>(response, "Failed to create AI conversation");
    } catch (error) {
      console.error("Error starting AI conversation", error);
      throw error;
    }
  },

  async getMessages(
    conversationId: string,
    options?: AiMessagesRequestOptions,
  ): Promise<AiMessagesPage> {
    if (!conversationId) {
      throw new Error("conversationId is required");
    }
    try {
      const response = await api.get(`/ai/get-messages/${conversationId}`, {
        params: {
          after: options?.after ?? undefined,
          before: options?.before ?? undefined,
          limit: options?.limit ?? 50,
        },
      });
      return extractResponseData<AiMessagesPage>(response, "Failed to load AI messages");
    } catch (error) {
      console.error("Error fetching AI messages", error);
      throw error;
    }
  },

  async sendMessage(
    payload: AiSendMessagePayload,
    abortSignal?: AbortSignal,
  ): Promise<AiSendMessageResponse> {
    try {
      const response = await api.post("/ai/send-message", payload, {
        signal: abortSignal,
      });
      return extractResponseData<AiSendMessageResponse>(response, "Failed to send AI message");
    } catch (error) {
      // Check if this was an abort/cancel
      if (error instanceof Error && error.name === "CanceledError") {
        throw error; // Re-throw cancel errors as-is
      }
      console.error("Error sending AI message", error);
      throw error;
    }
  },

  // Pre-warm the lot-context snapshot when the chat opens so the first message is fast. Best-effort.
  async warmContext(lotId: string): Promise<void> {
    if (!lotId) {
      return;
    }
    try {
      await api.post(`/ai/warm-context/${lotId}`, {});
    } catch (error) {
      // Non-fatal — warming is an optimization only.
      console.debug("Context warm failed", error);
    }
  },

  async cancelRequest(conversationId: string): Promise<{ cancelled: boolean; message: string }> {
    if (!conversationId) {
      throw new Error("conversationId is required");
    }
    try {
      const response = await api.post("/ai/cancel-request", { conversationId });
      return extractResponseData<{ cancelled: boolean; message: string }>(
        response,
        "Failed to cancel AI request",
      );
    } catch (error) {
      console.error("Error cancelling AI request", error);
      throw error;
    }
  },

  async confirmAgentAction(taskId: string): Promise<AiAgentActionConfirmResponse> {
    if (!taskId) {
      throw new Error("taskId is required");
    }
    try {
      const response = await api.post(`/ai/agent-actions/${taskId}/confirm`, {});
      return extractResponseData<AiAgentActionConfirmResponse>(response, "Failed to confirm agent action");
    } catch (error) {
      console.error("Error confirming agent action", error);
      throw buildAxiosError(error, "Failed to confirm agent action");
    }
  },

  async cancelAgentAction(taskId: string): Promise<AiAgentActionConfirmResponse> {
    if (!taskId) {
      throw new Error("taskId is required");
    }
    try {
      const response = await api.post(`/ai/agent-actions/${taskId}/cancel`, {});
      return extractResponseData<AiAgentActionConfirmResponse>(response, "Failed to cancel agent action");
    } catch (error) {
      console.error("Error cancelling agent action", error);
      throw buildAxiosError(error, "Failed to cancel agent action");
    }
  },

  /**
   * Fetches lightweight agent info (key, name, description) for enabled agents.
   * Used by the chat UI agent picker for @mentions.
   */
  async getAgentNames(): Promise<AiAgentNameItem[]> {
    try {
      const response = await api.get("/ai/get-agent-names");
      return extractResponseData<AiAgentNameItem[]>(response, "Failed to load agent names") ?? [];
    } catch (error) {
      console.error("Error fetching agent names", error);
      return [];
    }
  },
};

// AI Prediction service functions
export const aiPredictionService = {
  getAllPredictions: async (lotId: string) => {
    if (!lotId) return [];
    try {
      const response = await api.get(`/ai/prediction/all-predictions/${lotId}`);
      const predictions = response.data?.data;
      if (Array.isArray(predictions)) {
        return predictions;
      }
      return [];
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      console.error("Error fetching AI predictions list:", error);
      throw error;
    }
  },
  getPredictionByDate: async (lotId: string, targetDate: string) => {
    if (!lotId || !targetDate) return null;
    try {
      const response = await api.get(
        `/ai/prediction/prediction-by-date/${lotId}`,
        {
          params: { date: targetDate },
        }
      );
      return response.data?.data ?? null;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      console.error("Error fetching AI prediction by date:", error);
      throw error;
    }
  },
};

// Lot Pricing service functions
export const lotPricingService = {
  // Get latest pricing for a lot
  getLatestPricingByLotId: async (lotId: string) => {
    try {
      const response = await api.get(
        `/lotpricing/get-latest-pricing-by-lot-id/${lotId}`
      );
      return response.data.data; // Return the data object from the response
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        // Return null if no pricing exists for this lot
        return null;
      }
      console.error("Error fetching lot pricing:", error);
      throw error;
    }
  },

  // Update pricing for a lot
  updatePricing: async (lotId: string, pricingId: string, pricingData: any) => {
    try {
      // The backend expects the pricingId of the currently active pricing
      const response = await api.put(
        `/lotpricing/update-pricing-by-lot-id/${lotId}/${pricingId}`,
        pricingData
      );
      return response.data;
    } catch (error: any) {
      console.error("Error updating lot pricing:", error);
      throw error;
    }
  },

  // Get all pricing records for a lot
  getAllPricingsByLotId: async (lotId: string) => {
    try {
      const response = await api.get(
        `/lotpricing/get-general-pricing-by-lot-id/${lotId}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        // Return empty array if no pricing exists for this lot
        return [];
      }
      console.error("Error fetching lot pricings:", error);
      throw error;
    }
  },

  // Update or create pricing for a lot
  updateOrCreatePricing: async (lotId: string, pricingData: any) => {
    try {
      const response = await api.put(
        `/lotpricing/update-or-create-pricing-by-lot-id/${lotId}`,
        pricingData
      );
      return response.data;
    } catch (error: any) {
      console.error("Error updating lot pricing:", error);
      throw error;
    }
  },

  // Get advanced pricing for a lot
  getAdvancedPricingByLotId: async (lotId: string) => {
    try {
      const response = await api.get(
        `/lotadvancedpricing/get-advanced-pricing-by-lot-id/${lotId}`
      );
      return response.data;
    } catch (error: any) {
      // Return empty array if 404 (no pricing found)
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }
      throw error;
    }
  },

  // Update all advanced pricing for a lot (replaces existing entries)
  updateAdvancedPricing: async (lotId: string, pricingData: any[]) => {
    const response = await api.put(
      `/lotadvancedpricing/update-lot-advanced-pricing/${lotId}`,
      pricingData
    );
    return response.data;
  },

  // Delete all advanced pricing for a lot
  deleteAllAdvancedPricing: async (lotId: string) => {
    await api.delete(`/lotadvancedpricing/delete-all-by-lot-id/${lotId}`);
  },

  // New methods for advanced settings state
  getAdvancedSettingsState: async (lotId: string) => {
    try {
      const response = await api.get(
        `/lotadvancedpricing/advanced-settings-state/${lotId}`
      );
      return response.data.enabled;
    } catch (error) {
      // Default to false if error
      console.error("Error getting advanced settings state:", error);
      return false;
    }
  },

  setAdvancedSettingsState: async (lotId: string, enabled: boolean) => {
    const response = await api.put(
      `/lotadvancedpricing/advanced-settings-state/${lotId}`,
      { enabled }
    );
    return response.data.enabled;
  },
};

// Session service functions
export const sessionService = {
  // Get all sessions
  getAllSessions: async () => {
    const response = await api.get("/parkingsessions/get-all-active-sessions");
    return response.data.data;
  },

  // Get session by ID
  getSessionById: async (sessionId: string) => {
    const response = await api.get(`/parkingsessions/get-by-id/${sessionId}`);
    return response.data.data;
  },

  // Get sessions by lot ID
  getSessionsByLot: async (lotId: string) => {
    const response = await api.get(
      "/parkingsessions/get-all-active-sessions-by-lot",
      {
        params: { lotId },
      }
    );
    return response.data.data;
  },

  // Create new session
  createSession: async (sessionData: any) => {
    const response = await api.post(
      "/parkinglots/sessions/create",
      sessionData
    );
    return response.data;
  },

  // Update session
  updateSession: async (sessionId: string, sessionData: any) => {
    const response = await api.put(
      `/parkinglots/sessions/update/${sessionId}`,
      sessionData
    );
    return response.data;
  },

  // Validate session
  validateSession: async (
    sessionId: string,
    lotId: string,
    validated: boolean,
    modifiedBy: string
  ) => {
    const endpoint = validated
      ? "/parkingsessions/validate-session"
      : "/parkingsessions/invalidate-session";

    console.log(`Calling ${endpoint} with:`, { lotId, sessionId, modifiedBy });

    const response = await api.put(endpoint, null, {
      params: {
        lotId,
        sessionId,
        [validated ? "validatedBy" : "invalidatedBy"]: modifiedBy,
      },
    });
    return response.data;
  },

  // End session
  endSession: async (
    sessionId: string,
    exitDeviceId: string,
    modifiedBy: string
  ) => {
    console.log(`Calling end session API for session ${sessionId}`);
    const response = await api.put(
      `/parkinglots/sessions/end/${sessionId}`,
      null,
      {
        params: {
          exitDeviceId: exitDeviceId,
          modifiedBy: modifiedBy,
        },
      }
    );
    return response.data;
  },

  // Delete session
  deleteSession: async (sessionId: string) => {
    console.log(`Deleting session with ID: ${sessionId}`);
    await api.delete(`/parkinglots/sessions/delete/${sessionId}`);
  },

  // Force end session
  forceEndSession: async (
    sessionId: string,
    lotId: string,
    forceEndedBy: string
  ) => {
    console.log(`Calling force-end-session with:`, {
      lotId,
      sessionId,
      forceEndedBy,
    });

    const response = await api.put("/parkingsessions/force-end-session", null, {
      params: {
        lotId,
        sessionId,
        forceEndedBy,
        exitTime: new Date().toISOString(),
      },
    });
    return response.data;
  },

  // Force end all sessions for a lot
  forceEndAllSessions: async (lotId: string) => {
    console.log(`Calling force-end-all-sessions with:`, { lotId });

    const response = await api.put(
      "/parkingsessions/force-end-all-sessions",
      null,
      {
        params: { lotId },
      }
    );
    return response.data;
  },

  // Get sessions by lot with offset-based pagination (superadmin)
  getSessionsByLotOffsetPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/parkingsessions/get-sessions-by-lot-id-offset-paginated/${lotId}?${searchParams.toString()}`
    );
    return response.data.data;
  },

  // Get active sessions by lot with offset-based pagination
  getActiveSessionsByLotPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("lotId", lotId);
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/parkingsessions/get-active-sessions-by-lot-paginated?${searchParams.toString()}`
    );
    return response.data.data;
  },
};

// ALPR service functions
export const alprService = {
  // Get ALPR entries by lot with offset-based pagination (superadmin)
  getAlprByLotOffsetPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/alpr/get-alpr-by-lot-id-offset-paginated/${lotId}?${searchParams.toString()}`
    );
    return response.data.data;
  },
};

// Add these interfaces near the top with other interfaces
interface RegistryEntry {
  registryId: string;
  lotId: string;
  plateNumber: string;
  name: string;
  email: string;
  phone: string;
  permitId?: string | null;
  validFrom: string;
  validUntil: string;
  createdOn: string;
  createdBy: string;
  modifiedOn: string;
  modifiedBy: string;
  isDeleted: boolean;
}

// Permit types (mirror backend PermitResponseDTO / Permit module)
export type PermitType = "FREE" | "PERCENTAGE_DISCOUNT" | "FIXED_DISCOUNT";

export interface Permit {
  permitId: string;
  lotId: string;
  permitName: string;
  description?: string | null;
  permitType: PermitType;
  discountPercent?: number | null;
  discountAmount?: number | null;
  currency?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  validWeekDays?: number[] | null; // 0 = Sunday ... 6 = Saturday
  isDefault: boolean;
  createdOn?: string;
  createdBy?: string;
  modifiedOn?: string;
  modifiedBy?: string;
}

export interface CreatePermitPayload {
  lotId: string;
  permitName: string;
  description?: string | null;
  permitType: PermitType;
  discountPercent?: number | null;
  discountAmount?: number | null;
  currency?: string | null;
  validFrom?: string | null;
  validUntil?: string | null;
  validWeekDays?: number[] | null; // 0 = Sunday ... 6 = Saturday
  isDefault?: boolean;
}

export type UpdatePermitPayload = Omit<CreatePermitPayload, "lotId">;

interface RegistryResponse {
  success: boolean;
  data: RegistryEntry[];
  message: string;
  code: number;
}

interface IgnoredAlprResponse {
  ignoredAlprId: string;
  lotId: string;
  ignoredString: string;
  createdOn?: string;
  createdBy?: string;
  modifiedOn?: string;
  modifiedBy?: string;
  isDeleted?: boolean;
}

// Registry service functions
export const registryService = {
  // Get all registry entries for a lot
  getRegistryByLot: async (lotId: string): Promise<RegistryEntry[]> => {
    const response = await api.get<RegistryResponse>(
      `/lotplateRegistry/get-by-lot/${lotId}`
    );
    return response.data.data;
  },

  // Create a new registry entry
  createRegistryEntry: async (entry: {
    lotId: string;
    plateNumber: string;
    name: string;
    email?: string;
    phone?: string;
    createdBy: string;
    permitId?: string | null;
  }) => {
    const response = await api.post("/lotplateRegistry/create", entry);
    return response.data;
  },

  // Update an existing registry entry
  updateRegistryEntry: async (
    registryId: string,
    entry: {
      lotId: string;
      plateNumber: string;
      name: string;
      email: string;
      phone: string;
      modifiedBy: string;
      permitId?: string | null;
    }
  ) => {
    const response = await api.put(
      `/lotplateRegistry/update/${registryId}`,
      entry
    );
    return response.data;
  },

  // Delete a registry entry - updated endpoint
  deleteRegistryEntry: async (
    lotId: string,
    registryId: string,
    deletedBy: string
  ) => {
    await api.delete(`/lotplateRegistry/delete`, {
      data: { lotId, registryId, deletedBy },
    });
  },

  // Check if a plate is registered for a lot
  isPlateRegistered: async (lotId: string, plateNumber: string) => {
    const response = await api.get(
      `/lotplateRegistry/is-registered/${lotId}/${plateNumber}`
    );
    return response.data;
  },

  // Get registry entries by lot with offset-based pagination
  getRegistryByLotPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<RegistryEntry>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/lotplateRegistry/get-by-lot-paginated/${lotId}?${searchParams.toString()}`
    );
    return response.data.data;
  },
};

// Permit service functions
export const permitService = {
  // Get all (non-deleted) permits for a lot
  getPermitsByLot: async (lotId: string): Promise<Permit[]> => {
    const response = await api.get(`/permit/get-by-lot/${lotId}`);
    return response.data.data;
  },

  // Get a single permit by id
  getPermitById: async (permitId: string): Promise<Permit> => {
    const response = await api.get(`/permit/get/${permitId}`);
    return response.data.data;
  },

  // Create a new permit
  createPermit: async (payload: CreatePermitPayload): Promise<Permit> => {
    const response = await api.post("/permit/create", payload);
    return response.data.data;
  },

  // Update an existing permit
  updatePermit: async (
    permitId: string,
    payload: UpdatePermitPayload
  ): Promise<Permit> => {
    const response = await api.put(`/permit/update/${permitId}`, payload);
    return response.data.data;
  },

  // Soft-delete a permit (body carries the permitId)
  deletePermit: async (permitId: string): Promise<void> => {
    await api.delete(`/permit/delete`, { data: { permitId } });
  },
};

// Ignored ALPR service functions
export const ignoredAlprService = {
  getIgnoredStringsByLot: async (lotId: string): Promise<string[]> => {
    const response = await api.get(
      `/ignored-alpr/get-all-ignored-alpr-strings-by-lot/${lotId}`
    );
    const payload = unwrapApiData<any[]>(response.data);
    const records = Array.isArray(payload) ? payload : [];

    return records
      .map((record) =>
        typeof record === "string" ? record : record?.ignoredString
      )
      .filter((value): value is string => Boolean(value))
      .map((value) => value.toUpperCase());
  },

  bulkSaveIgnoredStrings: async (
    lotId: string,
    ignoredStrings: string[],
    performedBy: string
  ) => {
    const payload = {
      lotId,
      ignoredStrings,
      performedBy,
    };
    const response = await api.post("/ignored-alpr/bulk-save", payload);
    return response.data;
  },
};

// Lot Daily Data service functions
export const lotDailyDataService = {
  // Get dashboard metrics for a lot
  getDashboardMetrics: async (
    lotId: string,
    startDate: string,
    endDate: string
  ) => {
    try {
      const response = await api.get(
        `/analytics/daily/get-lot-daily-analytics-by-date-range`,
        {
          params: {
            lotId,
            startDate,
            endDate,
          },
        }
      );

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        // Return empty array if no data exists
        return [];
      }
      console.error("Error fetching dashboard metrics:", error);
      throw error;
    }
  },

  // Get hourly data for a specific date
  getLotHourlyByDate: async (lotId: string, date: string) => {
    try {
      const response = await api.get(
        `/analytics/hourly/get-lot-hourly-by-date`,
        {
          params: {
            lotId,
            date,
          },
        }
      );

      if (response.data.success) {
        return response.data;
      }
      return { success: true, data: [] };
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        // Return empty array if no data exists
        return { success: true, data: [] };
      }
      console.error("Error fetching hourly metrics:", error);
      throw error;
    }
  },
};

// Devices service functions
export const devicesService = {
  // Get all devices for a lot
  getDevicesByLot: async (lotId: string) => {
    try {
      const response = await api.get(`/devices/get-by-lot/${lotId}`);

      // Debug the response structure
      console.log("Devices API Response:", response);
      console.log("Devices Response data:", response.data);

      // Handle case where response.data might not be an array
      let devicesData = response.data;
      if (!Array.isArray(devicesData)) {
        // If data is not an array, try to extract the array from the response
        if (devicesData && typeof devicesData === "object") {
          // Try common response structures
          devicesData =
            devicesData.data || devicesData.devices || devicesData.items || [];
        } else {
          devicesData = [];
        }
      }

      // Transform the response data to match the expected Device interface
      const devices = devicesData.map((device: any) => ({
        deviceId: device.deviceId,
        deviceStatus: device.deviceStatus || "Offline", // Default to Offline if status not provided
      }));

      return devices;
    } catch (error) {
      console.error("Error fetching devices by lot ID:", error);
      throw error;
    }
  },
};

// Password Reset service functions
export const passwordResetService = {
  // Request password reset
  requestPasswordReset: async (email: string) => {
    const response = await api.post("/password-reset/request", { email });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post("/password-reset/reset", {
      token,
      newPassword,
    });
    return response.data;
  },
};

// Auth service functions
export const authService = {
  // ... existing auth functions ...

  // Request password reset
  requestPasswordReset: async (email: string) => {
    const response = await api.post("/password-reset/request", { email });
    return response.data;
  },

  // Reset password with token
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post("/password-reset/reset", {
      token,
      newPassword,
    });
    return response.data;
  },
};

// Health check service
export const healthService = {
  // Simple health check to verify server connectivity
  checkServerStatus: async (): Promise<boolean> => {
    try {
      const response = await api.get("/health", {
        timeout: 5000,
      } as CustomAxiosRequestConfig);
      return response.status === 200;
    } catch (error) {
      console.error("Health check failed:", error);
      return false;
    }
  },
};

// Notification service functions
export const notificationService = {
  // Get notifications by lot ID
  getNotificationsByLot: async (lotId: string) => {
    const response = await api.get(
      `/mvpnotifications/get-notification-by-lot/${lotId}`
    );
    return response.data.data;
  },

  // Create a new notification
  createNotification: async (payload: any) => {
    const response = await api.post(
      `/mvpnotifications/create-notification`,
      payload
    );
    return response.data;
  },

  // Get notifications by lot ID with pagination
  getNotificationsByLotPaginated: async (
    lotId: string,
    cursor?: string,
    limit?: number
  ) => {
    const params = new URLSearchParams();
    if (cursor) {
      params.append("cursor", cursor);
    }
    if (limit) {
      params.append("limit", limit.toString());
    }

    const response = await api.get(
      `/mvpnotifications/get-notification-by-lot-paginated/${lotId}?${params.toString()}`
    );
    return response.data;
  },

  // Get notifications by lot ID with offset-based pagination
  getNotificationsByLotOffsetPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/mvpnotifications/get-notification-by-lot-offset-paginated/${lotId}?${searchParams.toString()}`
    );
    return response.data.data;
  },

  // Mark notification as read
  markAsRead: async (lotId: string, notificationId: string) => {
    const response = await api.put(
      `/mvpnotifications/mark-read/${lotId}/${notificationId}`
    );
    return response.data;
  },

  // Mark notification as unread
  markAsUnread: async (lotId: string, notificationId: string) => {
    const response = await api.put(
      `/mvpnotifications/mark-unread/${lotId}/${notificationId}`
    );
    return response.data;
  },

  // Soft delete notification
  softDelete: async (lotId: string, notificationId: string) => {
    const response = await api.delete(
      `/mvpnotifications/soft-delete/${lotId}/${notificationId}`
    );
    return response.data;
  },

  // Count unread notifications by lot
  countUnreadNotificationsByLot: async (lotId: string) => {
    try {
      const response = await api.get(
        `/mvpnotifications/count-unread-by-lot/${lotId}`
      );
      return response.data.data;
    } catch (error: any) {
      // If endpoint doesn't exist yet (404), return -1 to indicate "coming soon"
      if (error.response?.status === 404) {
        return -1;
      }
      // For other errors, return 0 to avoid breaking the UI
      console.error("Error fetching notification count:", error);
      return 0;
    }
  },
};

// Lot Advanced Pricing service functions
export const lotAdvancedPricingService = {
  // Get active advanced pricing for a lot
  getActiveAdvancedPricing: async (lotId: string) => {
    try {
      const response = await api.get(
        `/lotadvancedpricing/get-active-advanced-pricing/${lotId}`
      );
      return response.data.data;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return [];
      }
      console.error("Error fetching advanced pricing:", error);
      throw error;
    }
  },

  // Create new advanced pricing entries
  createAdvancedPricing: async (
    lotId: string,
    pricingData: any[],
    createdBy: string
  ) => {
    const response = await api.post(
      `/lotadvancedpricing/create-lot-advanced-pricing/${lotId}?createdBy=${createdBy}`,
      pricingData
    );
    return response.data;
  },

  // Delete all active advanced pricing for a lot
  deleteActiveAdvancedPricing: async (lotId: string, modifiedBy: string) => {
    const response = await api.delete(
      `/lotadvancedpricing/delete-active-advanced-pricing/${lotId}?modifiedBy=${modifiedBy}`
    );
    return response.data;
  },

  // Get advanced pricing status for a lot
  getAdvancedPricingStatus: async (lotId: string): Promise<boolean> => {
    try {
      const response = await api.get(
        `/parkinglots/get-advanced-pricing-status/${lotId}`
      );
      return response.data.data;
    } catch (error) {
      console.error("Error getting advanced pricing status:", error);
      return false;
    }
  },

  // Change advanced pricing status
  changeAdvancedPricingStatus: async (
    lotId: string,
    modifiedBy: string,
    enabled: boolean
  ) => {
    const response = await api.put(
      `/parkinglots/change-advanced-pricing-status/${lotId}/${modifiedBy}?enabledBoolean=${enabled}`
    );
    return response.data.data;
  },
};

// Violation service functions
export const violationService = {
  // Get all violations by lot
  getAllViolationsByLot: async (lotId: string) => {
    const response = await api.get(`/violations/get-all-by-lot?lotId=${lotId}`);
    return response.data;
  },

  // Get all violations by lot with offset-based pagination
  getAllViolationsByLotPaginated: async (
    lotId: string,
    params: PaginationParams
  ): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    searchParams.append("lotId", lotId);
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    searchParams.append("sortDirection", params.sortDirection || "desc");
    if (params.search) searchParams.append("search", params.search);

    const response = await api.get(
      `/violations/get-all-by-lot-paginated?${searchParams.toString()}`
    );
    return response.data.data;
  },

  // Get open violations by plate
  getOpenViolationsByPlate: async (lotId: string, plateNumber: string) => {
    const response = await api.get(
      `/violations/get-open-violations-by-plate?lotId=${lotId}&plateNumber=${plateNumber}`
    );
    return response.data;
  },

  // Record a new violation
  recordViolation: async (violationData: {
    plateNumber: string;
    lotId: string;
    violationType: string;
    violationTimestamp: string;
    vehicleMake: string;
    vehicleModel: string;
    vehicleColor: string;
    totalViolationAmount: string;
    initialNotes: string;
  }) => {
    console.log("API call - recordViolation:", violationData);
    const response = await api.post(
      "/violations/record-violation",
      violationData
    );
    console.log("API response:", response.data);
    return response.data;
  },

  // Update single violation status
  updateSingleViolation: async (
    violationId: string,
    updatedBy: string,
    violationStatus: string,
    notes?: string
  ) => {
    const sanitizedViolationId = violationId.trim();
    if (!sanitizedViolationId) {
      throw new Error('Invalid violation id');
    }
    const params = new URLSearchParams({
      violationId: sanitizedViolationId,
      updatedBy,
      violationStatus,
    });
    if (notes) {
      params.append("notes", notes);
    }

    const response = await api.patch(
      `/violations/update-single-violation?${params.toString()}`
    );
    return response.data;
  },

  // Update all violations by plate
  updateAllViolationsByPlate: async (
    plateNumber: string,
    lotId: string,
    updatedBy: string,
    status: string,
    notes?: string
  ) => {
    const payload = {
      plateNumber,
      lotId,
      updatedBy,
      status,
      notes: notes || "",
    };

    const response = await api.patch(
      "/violations/update-all-violations-by-plate",
      payload
    );
    return response.data;
  },

  // Consolidated violators listing for the operator violators page.
  listViolators: async (params: {
    lotIds: string[];
    page: number;
    size: number;
    sortBy?: string;
    sortDirection?: string;
    search?: string;
    status?: "ACTIVE" | "DEPARTED" | "ALL";
  }): Promise<PageResponse<any>> => {
    const searchParams = new URLSearchParams();
    params.lotIds.forEach((id) => searchParams.append("lotIds", id));
    searchParams.append("page", params.page.toString());
    searchParams.append("size", params.size.toString());
    if (params.sortBy) searchParams.append("sortBy", params.sortBy);
    if (params.sortDirection) searchParams.append("sortDirection", params.sortDirection);
    if (params.search) searchParams.append("search", params.search);
    if (params.status) searchParams.append("status", params.status);

    const response = await api.get(`/violations/violators?${searchParams.toString()}`);
    return response.data.data;
  },

  // 7-day violation trend for the side panel chart.
  getViolatorTrend: async (plateNumber: string, lotIds: string[], days = 7) => {
    const searchParams = new URLSearchParams();
    lotIds.forEach((id) => searchParams.append("lotIds", id));
    searchParams.append("days", days.toString());
    const response = await api.get(
      `/violations/violators/${encodeURIComponent(plateNumber)}/trend?${searchParams.toString()}`
    );
    return response.data.data as { dayOfWeek: string; count: number }[];
  },

  // Update notes on a specific violation.
  updateViolationNotes: async (violationId: string, notes: string) => {
    const response = await api.patch(`/violations/${encodeURIComponent(violationId)}/notes`, { notes });
    return response.data;
  },
};

const unwrapApiData = <T>(payload: any): T => {
  if (!payload) {
    return payload as T;
  }

  if (Array.isArray(payload)) {
    return payload as T;
  }

  if (typeof payload === "object") {
    if ("data" in payload) {
      return (payload as { data: T }).data;
    }
    if ("items" in payload) {
      return (payload as { items: T }).items;
    }
  }

  return payload as T;
};

export interface DocRecord {
  docId: string;
  title: string;
  body: string;
  tags: string[];
  createdOn?: string;
  modifiedOn?: string;
}

export interface DocPayload {
  title: string;
  body: string;
  tags: string[];
}

export const fetchDocs = async (): Promise<DocRecord[]> => {
  const response = await api.get("/ai/get-docs");
  const docs = unwrapApiData<DocRecord[]>(response.data) ?? [];
  return docs.map((doc) => ({
    ...doc,
    tags: doc?.tags ?? [],
  }));
};

export const createDoc = async (payload: DocPayload): Promise<DocRecord> => {
  const response = await api.post("/ai/docs", payload);
  const doc = unwrapApiData<DocRecord>(response.data);
  return {
    ...doc,
    tags: doc?.tags ?? [],
  } as DocRecord;
};

export const updateDoc = async (docId: string, payload: DocPayload): Promise<DocRecord> => {
  const response = await api.put(`/ai/docs/${docId}`, payload);
  const doc = unwrapApiData<DocRecord>(response.data);
  return {
    ...doc,
    tags: doc?.tags ?? [],
  } as DocRecord;
};

export const deleteDoc = async (docId: string): Promise<void> => {
  await api.delete(`/ai/delete-doc/${docId}`);
};

export interface AgentRecord {
  agentId: string;
  agentKey: string;
  name: string;
  description?: string | null;
  prompt: string;
  enabled: boolean;
  payloadTemplate?: any;
  tags: string[];
  allowedFields?: string[] | null;
  lockedFields?: string[] | null;
  page?: string | null;
  prefetchEndpoint?: string | null;
  actionEndpoint?: string | null;
  actionMethod?: string | null;
  confirmLabel?: string | null;
  successMarkdown?: string | null;
  createdOn?: string;
  modifiedOn?: string;
}

export interface AgentPayload {
  agentId?: string;
  agentKey: string;
  name: string;
  description?: string;
  prompt: string;
  enabled?: boolean;
  payloadTemplate?: any;
  tags?: string[];
  allowedFields?: string[];
  lockedFields?: string[];
  page?: string;
  prefetchEndpoint?: string;
  actionEndpoint?: string;
  actionMethod?: string;
  confirmLabel?: string;
  successMarkdown?: string;
}

export const fetchAgents = async (): Promise<AgentRecord[]> => {
  const response = await api.get("/ai/get-agents");
  const agents = unwrapApiData<AgentRecord[]>(response.data) ?? [];
  return agents.map((agent) => ({
    ...agent,
    tags: agent?.tags ?? [],
    allowedFields: agent?.allowedFields ?? [],
    lockedFields: agent?.lockedFields ?? [],
    page: agent?.page ?? undefined,
  }));
};

export const saveAgent = async (payload: AgentPayload): Promise<AgentRecord> => {
  const response = await api.post("/ai/create-agent", payload);
  return unwrapApiData<AgentRecord>(response.data);
};

export const deleteAgent = async (agentId: string): Promise<void> => {
  await api.delete(`/ai/delete-agent/${agentId}`);
};

// Export the api instance for direct use
export default api;
